import { useState, useEffect, useRef, useCallback } from "react";
import { X, Edit3, Loader, Check, Search, ChevronDown } from "lucide-react";
import axiosInstance from "../../API/interceptor";
import { stripHtml } from "../../utils/sanitizeInput";

/* ── helpers ── */
// Cross-browser date+time picker (Firefox fix for datetime-local)
const inputCls = "w-full px-3 py-[10px] rounded-lg border border-[#E2E8F0] text-[13px] outline-none font-[inherit] box-border text-[#374151] bg-white h-[42px]";

function DateTimePicker({ value, onChange }) {
  const date = value ? value.split('T')[0] : '';
  const time = value ? (value.split('T')[1] || '') : '';
  const handle = (type, val) => {
    if (type === 'date') onChange(val ? `${val}T${time || '00:00'}` : '');
    else onChange(date ? `${date}T${val}` : '');
  };

  return (
    <div className="flex gap-[6px]">
      <input type="date" value={date} onChange={e => handle('date', e.target.value)}
        className={`${inputCls} flex-[3]`}/>
      <input type="time" value={time} onChange={e => handle('time', e.target.value)}
        className={`${inputCls} flex-[2]`}/>
    </div>
  );
}

const colorMap  = ["#6366F1","#8B5CF6","#EC4899","#F59E0B","#10B981","#3B82F6"];
const initColor = (n="") => colorMap[(n||"").charCodeAt(0) % colorMap.length];

function Av({ name="?", size=24 }) {
  const ini = (name||"?").split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
  return (
    <span className="inline-flex items-center justify-center rounded-full text-white font-semibold shrink-0"
      style={{ width:size, height:size, fontSize:size*0.35, background:initColor(name) }}>{ini}</span>
  );
}

/* ── Reusable user search dropdown ── */
function UserSelect({ label, value, onChange, multiple=false, required=false }) {
  const [open,    setOpen]    = useState(false);
  const [search,  setSearch]  = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debRef   = useRef(null);
  const abortRef = useRef(null);
  const wrapRef  = useRef(null);

  const doSearch = useCallback((term) => {
    clearTimeout(debRef.current);
    abortRef.current?.abort();
    debRef.current = setTimeout(async () => {
      abortRef.current = new AbortController();
      setLoading(true);
      try {
        const res = await axiosInstance.get("/task_assignment/get_users_all", {
          params: { q: term || "" },
          signal: abortRef.current.signal,
        });
        const data = Array.isArray(res.data) ? res.data : [];
        setResults(data.map(u => ({
          value: String(u.staffid),
          label: u.display_name || u.alias_name || String(u.staffid),
        })));
      } catch (e) {
        if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
        setResults([]);
      } finally { setLoading(false); }
    }, 300);
  }, []);

  useEffect(() => { if (open) doSearch(""); }, [open]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const isSelected = (val) => multiple
    ? (Array.isArray(value) ? value.some(v => v.value === val) : false)
    : value?.value === val;

  const toggle = (val, lbl) => {
    if (multiple) {
      const arr = Array.isArray(value) ? value : [];
      if (arr.some(v => v.value === val)) onChange(arr.filter(v => v.value !== val));
      else onChange([...arr, { value: val, label: lbl }]);
    } else {
      onChange({ value: val, label: lbl });
      setOpen(false);
    }
  };

  const displayLabel = () => {
    if (multiple) {
      const arr = Array.isArray(value) ? value : [];
      if (!arr.length) return <span className="text-[#94A3B8] text-[13px]">Select...</span>;
      return (
        <div className="flex flex-wrap gap-1">
          {arr.map(v => (
            <span key={v.value} className="bg-[#EEEDFE] text-[#3C3489] text-[11px] font-semibold px-2 py-[2px] rounded-full border-[0.5px] border-[#AFA9EC] flex items-center gap-1">
              {v.label}
              <button onClick={e => { e.stopPropagation(); onChange(arr.filter(x => x.value !== v.value)); }}
                className="bg-transparent border-none cursor-pointer text-[#3C3489] text-[13px] leading-none p-0">×</button>
            </span>
          ))}
        </div>
      );
    }
    if (value?.label) return <span className="text-[#374151] text-[13px]">{value.label}</span>;
    return <span className="text-[#94A3B8] text-[13px]">Select...</span>;
  };

  return (
    <div ref={wrapRef} className="relative">
      <label className="text-[12px] font-semibold text-[#64748B] block mb-1">
        {label}{required && <span className="text-[#EF4444]">*</span>}
      </label>
      <div onClick={() => setOpen(p => !p)}
        className={`min-h-[42px] px-3 py-[10px] rounded-lg cursor-pointer bg-white flex items-center justify-between gap-[6px] box-border border ${open ? "border-[rgb(124,132,207)]" : "border-[#E2E8F0]"}`}>
        <div className="flex-1 min-w-0">{displayLabel()}</div>
        <ChevronDown size={12} color="#94A3B8"
          className={`shrink-0 transition-transform duration-200 ${open ? "rotate-180" : "rotate-0"}`}/>
      </div>
      {open && (
        <div className="absolute top-[calc(100%+4px)] left-0 right-0 bg-white rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,.12)] z-[300] overflow-hidden border-[0.5px] border-[#E8EAF0]">
          <div className="p-[8px_10px] border-b-[0.5px] border-[#F1F5F9]">
            <div className="flex items-center gap-[6px] p-[5px_8px] rounded-[7px] border-[0.5px] border-[#E8EAF0] bg-[#F8FAFC]">
              <Search size={12} color="#94A3B8"/>
              <input autoFocus value={search}
                onChange={e => { setSearch(e.target.value); doSearch(e.target.value); }}
                placeholder="Search..."
                className="border-none outline-none bg-transparent text-[12px] text-[#374151] w-full font-[inherit]"/>
            </div>
          </div>
          <div className="max-h-[180px] overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center"><Loader size={16} color="rgb(124,132,207)"/></div>
            ) : results.length===0 ? (
              <div className="p-[14px_12px] text-center text-[12px] text-[#94A3B8]">No users found</div>
            ) : results.map(u => {
              const sel = isSelected(u.value);
              return (
                <div key={u.value} onClick={() => toggle(u.value, u.label)}
                  className={`flex items-center gap-[10px] p-[8px_12px] cursor-pointer border-b-[0.5px] border-[#F9FAFB] ${sel ? "bg-[#EEEDFE]" : "bg-white hover:bg-[#F8FAFC]"}`}>
                  {multiple && (
                    <div className={`w-[15px] h-[15px] rounded shrink-0 flex items-center justify-center border-2 ${sel ? "border-[rgb(124,132,207)] bg-[rgb(124,132,207)]" : "border-[#D1D5DB] bg-white"}`}>
                      {sel && <Check size={8} color="#fff"/>}
                    </div>
                  )}
                  <Av name={u.label} size={22}/>
                  <span className="text-[12px] text-[#374151]">{u.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Field wrapper ── */
const Field = ({ label, required, children, col="1" }) => (
  <div className={col === "2" ? "col-span-2" : "col-span-1"}>
    {label && (
      <label className="text-[12px] font-semibold text-[#64748B] block mb-1">
        {label}{required && <span className="text-[#EF4444]">*</span>}
      </label>
    )}
    {children}
  </div>
);

/* ── Category multi-select dropdown with search ── */
function CatSelect({ categories, value, onChange }) {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = categories.filter(c =>
    c.toLowerCase().includes(search.toLowerCase())
  );

  const isSelected = (cat) => value.includes(cat);

  const toggle = (cat) => {
    if (isSelected(cat)) onChange(value.filter(c => c !== cat));
    else onChange([...value, cat]);
  };

  return (
    <div ref={wrapRef} className="relative">
      {/* Trigger */}
      <div onClick={() => setOpen(p => !p)}
        className={`min-h-[38px] px-[10px] py-[6px] rounded-lg cursor-pointer bg-white flex items-center justify-between gap-[6px] box-border border ${open ? "border-[rgb(124,132,207)]" : "border-[#E2E8F0]"}`}>
        <div className="flex-1 min-w-0">
          {value.length === 0 ? (
            <span className="text-[#94A3B8] text-[13px]">Select categories...</span>
          ) : (
            <div className="flex flex-wrap gap-1">
              {value.map((cat, i) => (
                <span key={i} className="bg-[#EEEDFE] text-[#3C3489] text-[11px] font-semibold px-2 py-[2px] rounded-full border-[0.5px] border-[#AFA9EC] flex items-center gap-1">
                  {cat}
                  <button onClick={e => { e.stopPropagation(); toggle(cat); }}
                    className="bg-transparent border-none cursor-pointer text-[#3C3489] text-[13px] leading-none p-0">×</button>
                </span>
              ))}
            </div>
          )}
        </div>
        <ChevronDown size={12} color="#94A3B8"
          className={`shrink-0 transition-transform duration-200 ${open ? "rotate-180" : "rotate-0"}`}/>
      </div>

      {/* Dropdown */}
      {open && (
        <div className="absolute top-[calc(100%+4px)] left-0 right-0 bg-white rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,.12)] z-[300] overflow-hidden border-[0.5px] border-[#E8EAF0]">

          {/* Search */}
          <div className="p-[8px_10px] border-b-[0.5px] border-[#F1F5F9]">
            <div className="flex items-center gap-[6px] p-[5px_8px] rounded-[7px] border-[0.5px] border-[#E8EAF0] bg-[#F8FAFC]">
              <Search size={12} color="#94A3B8"/>
              <input autoFocus value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search categories..."
                className="border-none outline-none bg-transparent text-[12px] text-[#374151] w-full font-[inherit]"/>
            </div>
          </div>

          {/* List */}
          <div className="max-h-[200px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-[14px_12px] text-center text-[12px] text-[#94A3B8]">
                No categories found
              </div>
            ) : filtered.map((cat, i) => {
              const sel = isSelected(cat);
              return (
                <div key={i} onClick={() => toggle(cat)}
                  className={`flex items-center gap-[10px] p-[9px_12px] cursor-pointer border-b-[0.5px] border-[#F9FAFB] ${sel ? "bg-[#EEEDFE]" : "bg-white hover:bg-[#F8FAFC]"}`}>
                  <div className={`w-[15px] h-[15px] rounded shrink-0 flex items-center justify-center border-2 ${sel ? "border-[rgb(124,132,207)] bg-[rgb(124,132,207)]" : "border-[#D1D5DB] bg-white"}`}>
                    {sel && <Check size={8} color="#fff"/>}
                  </div>
                  <span className="text-[12px] text-[#374151]">{cat}</span>
                </div>
              );
            })}
          </div>

          {/* Footer — selected count */}
          {value.length > 0 && (
            <div className="p-[8px_12px] border-t-[0.5px] border-[#F1F5F9] bg-[#F8FAFC] flex justify-between items-center">
              <span className="text-[11px] text-[#64748B]">{value.length} selected</span>
              <button onClick={() => onChange([])}
                className="text-[11px] text-[#EF4444] bg-transparent border-none cursor-pointer font-[inherit]">
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── detect changed fields ── */
function detectChanges(original, current) {
  const changes = [];
  const check = (key, label) => {
    const o = JSON.stringify(original?.[key] ?? "");
    const c = JSON.stringify(current?.[key]  ?? "");
    if (o !== c) changes.push(label);
  };
  check("priority",              "Priority");
  check("start_date",            "Start Date");
  check("completion_sla",        "Due Date");
  check("created_by",            "Accountable");
  check("assigned_to",           "Responsible");
  check("observer",              "Consulted");
  check("informer",              "Informed");
  check("functional_categories", "Functional Categories");
  check("current_stage",         "Stage");
  check("task_type",             "Task Name");
  return changes;
}

/* ══ MAIN COMPONENT ══ */
export default function EditTaskModal({ task, onClose, onSuccess, showToast }) {
  const [form,        setForm]        = useState(null);
  const [original,    setOriginal]    = useState(null);
  const [categories,  setCategories]  = useState([]);
  const [stages,      setStages]      = useState([]);
  const [submitting,  setSubmitting]  = useState(false);
  const [loadingInit, setLoadingInit] = useState(true);
  const [errors,      setErrors]      = useState({});

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  /* ── Pre-fill form from task ── */
  useEffect(() => {
    const init = async () => {
      setLoadingInit(true);
      try {
        const [catRes, stageRes] = await Promise.allSettled([
          axiosInstance.get("/task_assignment/get_functional_categories"),
          task.product_code
            ? axiosInstance.get(`/task_assignment/get_work_stages/${task.product_code}`)
            : Promise.resolve({ data:[] })
        ]);

        if (catRes.status==="fulfilled") {
          const cats = Array.isArray(catRes.value.data) ? catRes.value.data : [];
          setCategories(cats);
        }
        if (stageRes.status==="fulfilled") {
          const s = stageRes.value.data;
          setStages(Array.isArray(s) ? s : (Array.isArray(s?.stages) ? s.stages : []));
        }

        // Build responsible array
        const assignedNames = Array.isArray(task.assigned_to_name)
          ? task.assigned_to_name
          : task.assigned_to_name ? [task.assigned_to_name] : [];
        const assignedIds = Array.isArray(task.assigned_to)
          ? task.assigned_to : task.assigned_to ? [task.assigned_to] : [];
        const responsibleArr = assignedIds.map((id, i) => ({
          value: String(id),
          label: assignedNames[i] || String(id)
        }));

        // Build consulted array
        const observerNames = Array.isArray(task.observer_to_name)
          ? task.observer_to_name : task.observer_to_name ? [task.observer_to_name] : [];
        const observerIds = Array.isArray(task.observer)
          ? task.observer : task.observer ? [task.observer] : [];
        const consultedArr = observerIds.map((id, i) => ({
          value: String(id),
          label: observerNames[i] || String(id)
        }));

        // Build informed array
        const informerNames = Array.isArray(task.informer_to_name)
          ? task.informer_to_name : task.informer_to_name ? [task.informer_to_name] : [];
        const informerIds = Array.isArray(task.informer)
          ? task.informer : task.informer ? [task.informer] : [];
        const informedArr = informerIds.map((id, i) => ({
          value: String(id),
          label: informerNames[i] || String(id)
        }));

        // Format dates for datetime-local
        const fmtForInput = (v) => {
          if (!v) return "";
          const dt = new Date(v);
          if (isNaN(dt)) return "";
          return dt.toISOString().slice(0, 16);
        };

        const initialForm = {
          task_type:            task.task_type || "",
          priority:             task.priority || "MEDIUM",
          start_date:           fmtForInput(task.start_date),
          completion_sla:       fmtForInput(task.completion_sla),
          created_by:           {
            value: String(task.task_created_by || task.task_created_by_id || ""),
            label: task.task_created_by_name || String(task.task_created_by || "")
          },
          assigned_to:          responsibleArr,
          observer:             consultedArr,
          informer:             informedArr,
          functional_categories: Array.isArray(task.functional_categories)
            ? task.functional_categories : [],
          current_stage:        task.current_stage || "",
        };

        setForm(initialForm);
        setOriginal(JSON.parse(JSON.stringify(initialForm)));
      } catch (e) {
        console.error("EditTaskModal init error:", e);
      } finally {
        setLoadingInit(false);
      }
    };
    init();
  }, [task]);

  const submit = async () => {
    if (!form) return;
    if (!form.task_type.trim()) {
      setErrors({ task_type: "Task name is required" });
      showToast("Task name is required", "error");
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const changes   = detectChanges(original, form);
      const changeLog = changes.length > 0
        ? "Task updated - " + changes.join(", ")
        : "Task updated - No changes detected";

      const fd = new FormData();
      fd.append("task_type",   stripHtml(form.task_type));
      fd.append("priority",    form.priority);
      fd.append("start_date",    form.start_date    ? form.start_date.replace("T", " ")    : "");
      fd.append("completionSLA", form.completion_sla ? form.completion_sla.replace("T", " ") : "");
      fd.append("created_by",  form.created_by?.value || "");
      fd.append("change_log",  changeLog);
      fd.append("has_changes", changes.length > 0 ? "1" : "0");

      const responsArr = Array.isArray(form.assigned_to) ? form.assigned_to : [];
      responsArr.forEach(u => fd.append("assignedTo", u.value));

      const consultArr  = Array.isArray(form.observer)    ? form.observer    : [];
      const origConsult = Array.isArray(original?.observer) ? original.observer : [];
      const observerChanged =
        JSON.stringify(consultArr.map(u => u.value).sort()) !==
        JSON.stringify(origConsult.map(u => u.value).sort());
      if (observerChanged) {
        consultArr.forEach(u => fd.append("reviewer", u.value));
        if (consultArr.length === 0) fd.append("reviewer_cleared", "1");
      }

      const informArr  = Array.isArray(form.informer)    ? form.informer    : [];
      const origInform = Array.isArray(original?.informer) ? original.informer : [];
      const informerChanged =
        JSON.stringify(informArr.map(u => u.value).sort()) !==
        JSON.stringify(origInform.map(u => u.value).sort());
      if (informerChanged) {
        informArr.forEach(u => fd.append("informer", u.value));
        if (informArr.length === 0) fd.append("informer_cleared", "1");
      }

      const catArr = Array.isArray(form.functional_categories) ? form.functional_categories : [];
      catArr.forEach(c => fd.append("functional_categories", c));

      if (form.current_stage) fd.append("current_stage", form.current_stage);

      const res = await axiosInstance.post(
        `/task_assignment/edit_task/${task._id}`,
        fd,
        { headers: { "Content-Type":"multipart/form-data" } }
      );

      if (res.data?.message || res.data?.success) {
        showToast("Task updated successfully!");
        onSuccess(); onClose();
      } else {
        showToast(res.data?.error || "Failed to update task","error");
      }
    } catch { showToast("Error updating task","error"); }
    finally   { setSubmitting(false); }
  };

  return (
    <>
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to   { transform: translateX(0); }
        }
      `}</style>

      {/* Backdrop */}
      <div onClick={onClose}
        className="fixed inset-0 z-[1600] bg-[rgba(15,23,42,.35)] backdrop-blur-[2px]"/>

      {/* Offcanvas panel */}
      <div onClick={e => e.stopPropagation()}
        className="fixed top-0 right-0 bottom-0 w-[min(700px,96vw)] bg-[#F8FAFC] z-[1601] flex flex-col animate-[slideInRight_.3s_cubic-bezier(.4,0,.2,1)]">

        {/* ── Header ── */}
        <div className="bg-[linear-gradient(135deg,rgb(124,132,207),rgb(187,196,255))] px-5 py-[14px] shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Edit3 size={16} color="#fff"/>
              <span className="text-[15px] font-bold text-white">Edit Task</span>
            </div>
            <button onClick={onClose}
              className="w-7 h-7 rounded-lg bg-[rgba(255,255,255,.15)] border border-[rgba(255,255,255,.3)] flex items-center justify-center cursor-pointer">
              <X size={14} color="#fff"/>
            </button>
          </div>
        </div>

        {/* ── Task name bar (editable) ── */}
        <div className="px-5 py-[10px] bg-white border-b-[0.5px] border-[#E8EAF0] shrink-0">
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] text-[#94A3B8] font-semibold uppercase tracking-[.06em]">
              Task Name<span className="text-[#EF4444]">*</span>
            </label>
            <span className="text-[11px] text-[#94A3B8] bg-[#F1F5F9] px-[7px] py-[1px] rounded-full">#{task.unique_seq_id}</span>
          </div>
          {form && (
            <input
              className={`${inputCls}${errors.task_type ? " border-red-400" : ""}`}
              placeholder="Enter task name"
              value={form.task_type}
              onChange={e => { set("task_type", e.target.value); if (errors.task_type) setErrors({}); }}
            />
          )}
          {errors.task_type && <div className="text-[11px] text-[#EF4444] mt-1">{errors.task_type}</div>}
        </div>

        {/* ── Body (scrollable) ── */}
        <div className="flex-1 overflow-y-auto p-5">
          {loadingInit ? (
            <div className="flex items-center justify-center py-10 gap-[10px] flex-col">
              <Loader size={24} color="rgb(124,132,207)"/>
              <span className="text-[13px] text-[#94A3B8]">Loading task data...</span>
            </div>
          ) : form && (
            <div className="grid grid-cols-2 gap-4">

              {/* Priority */}
              <Field label="Priority">
                <select value={form.priority}
                  onChange={e => set("priority", e.target.value)}
                  className={`${inputCls} cursor-pointer`}>
                  {["URGENT","HIGH","MEDIUM","LOW"].map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </Field>

              {/* Stage */}
              <Field label="Stage">
                <select value={form.current_stage}
                  onChange={e => set("current_stage", e.target.value)}
                  className={`${inputCls} cursor-pointer`}>
                  <option value="">Nothing Selected</option>
                  {stages.map((s, i) => {
                    const stageName = typeof s==="object"
                      ? (s.stage_name || s.name || String(s))
                      : String(s);
                    return <option key={i} value={stageName}>{stageName}</option>;
                  })}
                </select>
              </Field>

              {/* Start Date */}
              <Field label="Start Date">
                <DateTimePicker value={form.start_date}
                  onChange={v => set("start_date", v)}/>
              </Field>

              {/* Due Date */}
              <Field label="Due Date">
                <DateTimePicker value={form.completion_sla}
                  onChange={v => set("completion_sla", v)}/>
              </Field>

              {/* Accountable */}
              <Field label="">
                <UserSelect label="Accountable" value={form.created_by}
                  onChange={v => set("created_by", v)}/>
              </Field>

              {/* Responsible */}
              <Field label="">
                <UserSelect label="Responsible" value={form.assigned_to}
                  onChange={v => set("assigned_to", v)} multiple/>
              </Field>

              {/* Consulted */}
              <Field label="">
                <UserSelect label="Consulted" value={form.observer}
                  onChange={v => set("observer", v)} multiple/>
              </Field>

              {/* Informed */}
              <Field label="">
                <UserSelect label="Informed" value={form.informer}
                  onChange={v => set("informer", v)} multiple/>
              </Field>

              {/* Functional Categories — dropdown with search */}
              <Field label="Functional Categories" col="2">
                <CatSelect
                  categories={categories}
                  value={form.functional_categories}
                  onChange={v => set("functional_categories", v)}
                />
              </Field>

            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div className="flex gap-[10px] px-5 py-[14px] border-t-[0.5px] border-[#E8EAF0] bg-white shrink-0">
          <button onClick={onClose}
            className="flex-1 p-[10px] rounded-[10px] border-[0.5px] border-[#E8EAF0] bg-white text-[13px] font-semibold cursor-pointer text-[#64748B] font-[inherit]">
            Cancel
          </button>
          <button onClick={submit} disabled={submitting || loadingInit}
            className={`flex-[2] p-[10px] rounded-[10px] border-none bg-[rgb(124,132,207)] text-[13px] font-semibold text-white font-[inherit] flex items-center justify-center gap-[6px] ${submitting ? "cursor-not-allowed opacity-70" : "cursor-pointer opacity-100"}`}>
            {submitting
              ? <><Loader size={13}/> Updating...</>
              : <><Check size={13}/> Update Task</>}
          </button>
        </div>

      </div>
    </>
  );
}